import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router-dom';
import { lazy, Suspense, type ComponentType } from 'react';
import { AppShell } from './shell';
import { UserTypeProtectedRoute } from '../components/user-type-protected-route';
import { RouteAccessLevel } from '../config/route-config';
import { PublicOnlyRoute } from '../components/public-only-route';
import { lazyImportWithRetry } from '../lib/lazy-with-retry';
import { RouteErrorPage } from '../pages/route-error-page';

function lazyNamed<T extends ComponentType<any>>(
  loader: () => Promise<Record<string, unknown>>,
  exportName: string,
) {
  return lazy(async () => ({
    default: (await lazyImportWithRetry(loader))[exportName] as T,
  }));
}

const NotFoundPage = lazyNamed(() => import('../pages/not-found-page'), 'NotFoundPage');
const LandingPageV2 = lazyNamed(() => import('../features/landing/landing-page-v2'), 'LandingPageV2');
const SignInPage = lazyNamed(() => import('../features/auth/sign-in'), 'SignInPage');
const UnifiedRegistrationPage = lazyNamed(() => import('../features/auth/unified-registration-page'), 'UnifiedRegistrationPage');
const InvitationCodeValidator = lazyNamed(() => import('../features/auth/invitation-code-validator'), 'InvitationCodeValidator');
const VerifyOTPPage = lazyNamed(() => import('../features/auth/verify-otp'), 'VerifyOTPPage');
const NameCollectionStep = lazyNamed(() => import('../features/auth/name-collection-step'), 'NameCollectionStep');
const VerifyEmailPage = lazyNamed(() => import('../features/auth/verify-email-page'), 'VerifyEmailPage');
const ForgotPasswordPage = lazyNamed(() => import('../features/auth/forgot-password'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyNamed(() => import('../features/auth/reset-password'), 'ResetPasswordPage');
const VerifyEmailCallback = lazyNamed(() => import('../features/auth/verify-email-callback'), 'VerifyEmailCallback');
const PartnerVerifyEmailCallback = lazyNamed(() => import('../features/auth/partner-verify-email-callback'), 'PartnerVerifyEmailCallback');
const PartnerEmailConfirmedPage = lazyNamed(() => import('../features/auth/partner-email-confirmed'), 'PartnerEmailConfirmedPage');
const MemorialPage = lazyNamed(() => import('../features/memorial/memorial-page'), 'MemorialPage');
const CommunityVigilPage = lazyNamed(() => import('../features/vigil/community-vigil-page'), 'CommunityVigilPage');
const MemorialUnavailableRoutePage = lazyNamed(() => import('../features/memorial/memorial-unavailable-route-page'), 'MemorialUnavailableRoutePage');
const CreateMemorialPage = lazyNamed(() => import('../features/memorial/create-memorial-page'), 'CreateMemorialPage');
const EditMemorialPage = lazyNamed(() => import('../features/memorial/edit-memorial-page'), 'EditMemorialPage');
const AdminDashboard = lazyNamed(() => import('../features/memorial/admin-dashboard'), 'AdminDashboard');
const InviteToPage = lazyNamed(() => import('../features/memorial/invite-to-page'), 'InviteToPage');
const PageSettingsPage = lazyNamed(() => import('../features/memorial/settings/page-settings-page'), 'PageSettingsPage');
const VenueScreensPage = lazyNamed(() => import('../features/memorial/pages/venue-screens-page'), 'VenueScreensPage');
const ScreenPlayerPage = lazyNamed(() => import('../features/screen-player/screen-player-page'), 'ScreenPlayerPage');
const ObituaryWriterPage = lazyNamed(() => import('../features/obituary'), 'ObituaryWriterPage');
const ObituaryPrintPage = lazyNamed(() => import('../features/obituary'), 'ObituaryPrintPage');
const PublicObituaryPage = lazyNamed(() => import('../features/obituary'), 'PublicObituaryPage');
const EmbedObituaryPage = lazyNamed(() => import('../features/obituary'), 'EmbedObituaryPage');
const HomePage = lazyNamed(() => import('../features/home/home-page'), 'HomePage');
const LightACandlePage = lazyNamed(() => import('../features/candle/light-a-candle-page'), 'LightACandlePage');
const UserProfilePage = lazyNamed(() => import('../features/profile/user-profile'), 'UserProfilePage');
const ProfileCompletionPage = lazyNamed(() => import('../features/profile/profile-completion-page'), 'ProfileCompletionPage');
const SearchPage = lazyNamed(() => import('../features/search/search-page'), 'SearchPage');
const OnboardingIntentPage = lazyNamed(() => import('../features/onboarding/onboarding-intent-page'), 'OnboardingIntentPage');
const SearchPagesCategory = lazyNamed(() => import('../features/search/pages/search-pages-category'), 'SearchPagesCategory');
const SearchTagsCategory = lazyNamed(() => import('../features/search/pages/search-tags-category'), 'SearchTagsCategory');
const ATagHubPage = lazyNamed(() => import('../features/atags/atag-hub-page'), 'ATagHubPage');
const UserATagHubPage = lazyNamed(() => import('../features/atag-hub/atag-hub-page'), 'UserATagHubPage');
const SearchUsersCategory = lazyNamed(() => import('../features/search/pages/search-users-category'), 'SearchUsersCategory');
const IndividualATagManagePage = lazyNamed(() => import('../features/atags/pages/individual-atag-manage-page'), 'IndividualATagManagePage');
const CeremonyDetailPage = lazyNamed(() => import('../features/ceremonies/ceremony-detail'), 'CeremonyDetailPage');
const ChatsListPage = lazyNamed(() => import('../features/chat/chats-list'), 'ChatsListPage');
const ChatViewPage = lazyNamed(() => import('../features/chat/chat-view'), 'ChatViewPage');
const ChannelView = lazyNamed(() => import('../pages/channel-view'), 'ChannelView');
const ReportedPostReview = lazyNamed(() => import('../features/atags/pages/reported-post-review'), 'ReportedPostReview');
const ChannelsPage = lazyNamed(() => import('../features/channels/channels-page'), 'ChannelsPage');
const NotificationsPage = lazyNamed(() => import('../features/notifications/notifications-page'), 'NotificationsPage');
const SettingsPage = lazyNamed(() => import('../features/settings/settings-page'), 'SettingsPage');
const SafetySettingsPage = lazyNamed(() => import('../features/settings/safety-settings-page'), 'SafetySettingsPage');
const IdentityVerifiedPage = lazyNamed(() => import('../features/settings/identity-verified-page'), 'IdentityVerifiedPage');
const AboutPage = lazyNamed(() => import('../features/about/about-page'), 'AboutPage');
const FeaturesPage = lazyNamed(() => import('../features/about/features-page'), 'FeaturesPage');
const ContactPage = lazyNamed(() => import('../features/contact/contact-page'), 'ContactPage');
const HelpCenterPage = lazyNamed(() => import('../features/help/pages/help-center-page'), 'HelpCenterPage');
const CategoryPage = lazyNamed(() => import('../features/help/pages/category-page'), 'CategoryPage');
const ArticlePage = lazyNamed(() => import('../features/help/pages/article-page'), 'ArticlePage');
const PrivacyPolicyPage = lazyNamed(() => import('../features/legal/privacy-policy-page'), 'PrivacyPolicyPage');
const TermsOfServicePage = lazyNamed(() => import('../features/legal/terms-of-service-page'), 'TermsOfServicePage');
const AccountDeletionPage = lazyNamed(() => import('../features/legal/account-deletion-page'), 'AccountDeletionPage');
const DataDeletionPage = lazyNamed(() => import('../features/legal/data-deletion-page'), 'DataDeletionPage');
const ChildSafetyPage = lazyNamed(() => import('../features/legal/child-safety-page'), 'ChildSafetyPage');
const AccountPage = lazyNamed(() => import('../features/account/account-page'), 'AccountPage');
const OrdersPage = lazyNamed(() => import('../features/account/orders-page'), 'OrdersPage');
const MyPagesPage = lazyNamed(() => import('../features/pages/my-pages-page'), 'MyPagesPage');
const QRRedirect = lazyNamed(() => import('../pages/qr-redirect'), 'QRRedirect');
const PartnerVerifyEmailPage = lazyNamed(() => import('../features/partner/pages'), 'PartnerVerifyEmailPage');
const PartnerCompleteProfilePage = lazyNamed(() => import('../features/partner/pages'), 'PartnerCompleteProfilePage');
const PartnerDashboardPage = lazyNamed(() => import('../features/partner/pages'), 'PartnerDashboardPage');
const PartnerProfilePage = lazyNamed(() => import('../features/partner/pages'), 'PartnerProfilePage');
const CreatePartnerMemorialPage = lazyNamed(() => import('../features/partner/pages'), 'CreatePartnerMemorialPage');
const PartnerVerificationPendingPage = lazyNamed(() => import('../features/partner/pages'), 'PartnerVerificationPendingPage');
const PartnerVerificationRejectedPage = lazyNamed(() => import('../features/partner/pages'), 'PartnerVerificationRejectedPage');
const PartnerATagManagePage = lazyNamed(() => import('../features/partner/pages'), 'PartnerATagManagePage');
const EditPartnerMemorialPage = lazyNamed(() => import('../features/partner/pages/edit-partner-memorial-page'), 'EditPartnerMemorialPage');
const PartnersPublicPage = lazyNamed(() => import('../features/partner/pages/partners-public-page'), 'PartnersPublicPage');
const HonoredPagesPage = lazyNamed(() => import('../features/honored/honored-pages-page'), 'HonoredPagesPage');
const AccountSuspendedPage = lazyNamed(() => import('../features/account/account-suspended-page'), 'AccountSuspendedPage');
const PurchaseSuccessPage = lazyNamed(() => import('../features/account/purchase-return-pages'), 'PurchaseSuccessPage');
const PurchaseCancelPage = lazyNamed(() => import('../features/account/purchase-return-pages'), 'PurchaseCancelPage');
const ContactsPage = lazyNamed(() => import('../features/contacts/contacts-page'), 'ContactsPage');
const JoinPage = lazyNamed(() => import('../features/contacts/join-page'), 'JoinPage');
const ExplorePage = lazyNamed(() => import('../features/explore/explore-page'), 'ExplorePage');
const TestRegistrationPage = lazy(() => import('../pages/debug/test-registration'));

/**
 * Pathless layout so embed + print sit in the same tree as AppShell.
 * Some React Router versions match sibling roots inconsistently; nesting under
 * one parent guarantees /memorial/:slug/obituary/embed never renders AppShell.
 */
function AppRouteRoot() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  );
}

function LegacyDisplayRequestsRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/memorial/${slug}/venue-screens`} replace />;
}

export const router = createBrowserRouter([
  {
    element: <AppRouteRoot />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        // Top-level print route — bypasses AppShell entirely so no app chrome
        // (header, nav, shell-level overflow/flex containers) can ever interfere
        // with print layout. The page renders directly into #root with a clean,
        // self-contained HTML structure. Required to fix PDF rendering issues
        // we saw when the route lived under <AppShell />.
        path: '/memorial/:slug/obituary/print',
        element: <ObituaryPrintPage />,
      },
      {
        // Top-level embed route — must bypass AppShell so third-party iframes never
        // show LandingNavbar / AppHeader (same reasoning as print).
        path: '/memorial/:slug/obituary/embed',
        element: <EmbedObituaryPage />,
      },
      {
        // Venue TV player — full-screen, no app chrome, no user session needed.
        path: '/screen',
        element: <ScreenPlayerPage />,
      },
      {
        path: '/',
        element: <AppShell />,
        children: [
      {
        index: true,
        element: <PublicOnlyRoute><LandingPageV2 /></PublicOnlyRoute>,
      },
      {
        path: 'about',
        element: <AboutPage />,
      },
      {
        path: 'partners',
        element: <PartnersPublicPage />,
      },
      {
        path: 'honored',
        element: <HonoredPagesPage />,
      },
      {
        path: 'features',
        element: <FeaturesPage />,
      },
      {
        path: 'contact',
        element: <ContactPage />,
      },
      {
        path: 'support',
        element: <ContactPage />,
      },
      {
        path: 'help',
        element: <HelpCenterPage />,
      },
      {
        path: 'help/category/:slug',
        element: <CategoryPage />,
      },
      {
        path: 'help/article/:slug',
        element: <ArticlePage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPolicyPage />,
      },
      {
        path: 'terms',
        element: <TermsOfServicePage />,
      },
      {
        path: 'account-deletion',
        element: <AccountDeletionPage />,
      },
      {
        path: 'data-deletion',
        element: <DataDeletionPage />,
      },
      {
        path: 'child-safety',
        element: <ChildSafetyPage />,
      },
      {
        path: 'm/:shortCode',
        element: <QRRedirect />,
      },
      {
        path: 'sign-in',
        element: <SignInPage />,
      },
      {
        path: 'invite',
        element: <InvitationCodeValidator />,
      },
      {
        path: 'join/:inviteCode',
        element: <JoinPage />,
      },
      {
        path: 'sign-up',
        element: <UnifiedRegistrationPage />,
      },
      // Partner Registration - Uses unified registration page
      {
        path: 'partner/register',
        element: <UnifiedRegistrationPage />,
      },
      {
        path: 'partner/verify-email',
        element: <PartnerVerifyEmailCallback />,
      },
      {
        path: 'partner/email-confirmed',
        element: <PartnerEmailConfirmedPage />,
      },
      {
        path: 'partner/verification-pending',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.PARTNER_ONLY}
            requireEmailVerified={true}
            requirePartnerVerified={false}
          >
            <PartnerVerificationPendingPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'partner/verification-rejected',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.PARTNER_ONLY}
            requireEmailVerified={true}
          >
            <PartnerVerificationRejectedPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'partner/complete-profile',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.PARTNER_ONLY}
            requireEmailVerified={true}
            requirePartnerVerified={false}
          >
            <PartnerCompleteProfilePage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'partner/dashboard',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.PARTNER_ONLY}
            requireEmailVerified={true}
            requirePartnerVerified={true}
          >
            <PartnerDashboardPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'partner/profile',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.PARTNER_ONLY}
            requireEmailVerified={true}
          >
            <PartnerProfilePage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'partner/panel',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.PARTNER_ONLY}
            requireEmailVerified={true}
            requirePartnerVerified={true}
          >
            <PartnerDashboardPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'partner/atags/:tagId',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.PARTNER_ONLY}
            requireEmailVerified={true}
            requirePartnerVerified={true}
          >
            <PartnerATagManagePage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'partner/pages/create',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.PARTNER_ONLY}
            requireEmailVerified={true}
            requirePartnerVerified={true}
          >
            <CreatePartnerMemorialPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'partner/pages/:pageId/edit',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.PARTNER_ONLY}
            requireEmailVerified={true}
            requirePartnerVerified={true}
          >
            <EditPartnerMemorialPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'debug/test-registration',
        element: <TestRegistrationPage />,
      },
      {
        path: 'account-suspended',
        element: <AccountSuspendedPage />,
      },
      {
        path: 'verify-otp',
        element: <VerifyOTPPage />,
      },
      {
        path: 'name-step',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={false}
          >
            <NameCollectionStep />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'welcome',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={false}
          >
            <OnboardingIntentPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'verify-email',
        element: <VerifyEmailPage />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />,
      },
      {
        path: 'auth/verify-email-callback',
        element: <VerifyEmailCallback />,
      },
      {
        path: 'home',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.REGULAR_ONLY}
            requireEmailVerified={true}
          >
            <HomePage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'explore',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.REGULAR_ONLY}
            requireEmailVerified={true}
          >
            <ExplorePage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'light-a-candle',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.REGULAR_ONLY}
            requireEmailVerified={true}
          >
            <LightACandlePage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'search',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <SearchPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'search/pages',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <SearchPagesCategory />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'search/tags',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <SearchTagsCategory />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'search/users',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <SearchUsersCategory />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'atag-hub',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.REGULAR_ONLY}
            requireEmailVerified={true}
          >
            <UserATagHubPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'atag/:slug',
        element: <ATagHubPage />,
      },
      {
        path: 'atag/manage/:tagId',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <IndividualATagManagePage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'chats',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <ChatsListPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'chat/:chatId',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <ChatViewPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'channels',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <ChannelsPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'channels/:slug',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <ChannelView />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'channels/:channelId/post/:postId/review',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <ReportedPostReview />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'notifications',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <NotificationsPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'contacts',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <ContactsPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: <Navigate to="/account" replace />,
      },
      {
        path: 'profile/complete',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={false}
          >
            <ProfileCompletionPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'profile/:slug',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <UserProfilePage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <SettingsPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'settings/safety',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <SafetySettingsPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        // Public route — must be accessible without auth.
        // The in-app browser (mobile) has no session; the page handles both
        // authenticated (desktop) and unauthenticated (QR-code / mobile) cases.
        path: 'settings/identity-verified',
        element: <IdentityVerifiedPage />,
      },
      {
        path: 'my-pages',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.REGULAR_ONLY}
            requireEmailVerified={true}
          >
            <MyPagesPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'account',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <AccountPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'orders',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <OrdersPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'purchase/success',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <PurchaseSuccessPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'purchase/cancel',
        element: <PurchaseCancelPage />,
      },
      {
        path: 'vigil/:slug',
        element: <CommunityVigilPage />,
      },
      {
        path: 'memorial/unavailable',
        element: <MemorialUnavailableRoutePage />,
      },
      {
        path: 'memorial/create',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.REGULAR_ONLY}
            requireEmailVerified={true}
          >
            <CreateMemorialPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'memorial/:slug/edit',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <EditMemorialPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'memorial/:slug/admin',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <AdminDashboard />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'memorial/:slug/invite',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <InviteToPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'memorial/:slug/settings',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <PageSettingsPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'memorial/:slug/venue-screens',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <VenueScreensPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'memorial/:slug/display-requests',
        element: <LegacyDisplayRequestsRedirect />,
      },
      {
        // Public canonical obituary page — no auth required
        path: 'memorial/:slug/obituary',
        element: <PublicObituaryPage />,
      },
      {
        // Owner/admin writer — auth required; renamed from /obituary to /obituary/edit
        path: 'memorial/:slug/obituary/edit',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={true}
          >
            <ObituaryWriterPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: 'memorial/:slug',
        element: <MemorialPage />,
      },
      {
        path: 'ceremony/:ceremonyId',
        element: (
          <UserTypeProtectedRoute
            accessLevel={RouteAccessLevel.AUTHENTICATED}
            requireEmailVerified={false}
          >
            <CeremonyDetailPage />
          </UserTypeProtectedRoute>
        ),
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
        ],
      },
    ],
  },
]);
